import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Eye, EyeOff, UploadCloud, History, ImageIcon, Film, X, ExternalLink, Plus, Trash2 } from "lucide-react";
import { api, apiError, fmtDate, API, plainToHtml, htmlToPlain } from "../lib/api";
import { PageHeader } from "./AdminLayout";
import { RichTextEditor } from "../components/admin/RichTextEditor";
import { MediaPicker } from "../components/admin/MediaPicker";
import { RichText } from "../components/RichText";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const SECTION_LABELS = {
    hero: "Accueil — Hero", marquee: "Accueil — Bandeau défilant", video: "Accueil — Vidéo", value: "Accueil — Proposition de valeur",
    bento_sport: "Accueil — Carte « Coaching sportif IA »", bento_nutrition: "Accueil — Carte « Nutrition sur mesure »",
    bento_suivi: "Accueil — Carte « Suivi en temps réel »", bento_balance: "Accueil — Carte « Balance connectée »",
    steps: "Accueil — Parcours (intro)", pillars: "Accueil — Piliers (intro)",
    pillar_sport: "Pilier — Programme sportif", pillar_nutrition: "Pilier — Nutrition", pillar_suivi: "Pilier — Suivi corporel", pillar_balance: "Pilier — Balance",
    step_1: "Étape 1", step_2: "Étape 2", step_3: "Étape 3", step_4: "Étape 4", step_5: "Étape 5", step_6: "Étape 6", step_7: "Étape 7", step_8: "Étape 8",
    home_shop: "Accueil — Mise en avant Boutique", pricing: "Accueil — Tarifs (intro)", testimonials: "Accueil — Témoignages (intro)",
    faq: "Accueil — FAQ (intro)", download: "Accueil — Téléchargement", contact_intro: "Accueil — Contact (intro)",
    fonctionnement_hero: "Page Fonctionnement — En-tête", fonctionnement_outro: "Page Fonctionnement — Bloc de conclusion",
    fonctionnalites_hero: "Page Fonctionnalités — En-tête",
    boutique_hero: "Page Boutique — En-tête", avis_hero: "Page Avis — En-tête",
};
const PAGE_LABELS = { confidentialite: "Politique de confidentialité", "mentions-legales": "Mentions légales", cookies: "Politique cookies" };

// Capacités médias par section : uniquement lorsque le design public prévoit un média.
const MEDIA_CAPS = {
    hero: { image: true },
    video: { video: true },
    bento_sport: { image: true }, bento_nutrition: { image: true }, bento_suivi: { image: true }, bento_balance: { image: true },
    pillar_sport: { image: true }, pillar_nutrition: { image: true }, pillar_suivi: { image: true }, pillar_balance: { image: true },
};

const PREVIEW_PATH = {
    fonctionnement_hero: "/fonctionnement", fonctionnement_outro: "/fonctionnement",
    fonctionnalites_hero: "/fonctionnalites",
    pillar_sport: "/fonctionnalites", pillar_nutrition: "/fonctionnalites", pillar_suivi: "/fonctionnalites", pillar_balance: "/fonctionnalites",
    boutique_hero: "/boutique", avis_hero: "/avis",
};

function SectionEditor({ section, library, onClose, onSaved }) {
    const base = section.draft || section.published || {};
    const [form, setForm] = useState({ ...base });
    const [rich, setRich] = useState(base.body_format === "html" && base.body_html ? base.body_html : plainToHtml(base.body));
    const [showHistory, setShowHistory] = useState(false);
    const [picker, setPicker] = useState(null); // "image" | "video" | null
    const caps = MEDIA_CAPS[section.key] || {};
    const mediaById = useMemo(() => Object.fromEntries(library.map((m) => [m.media_id, m])), [library]);
    const curImage = form.image_media_id ? mediaById[form.image_media_id] : null;
    const curVideo = form.video_media_id ? mediaById[form.video_media_id] : null;

    const save = async (publish) => {
        try {
            const payload = { ...form, body_html: rich, body: htmlToPlain(rich) || (form.body || "") };
            await api.patch(`/admin/sections/${section.key}`, payload);
            if (publish) await api.post(`/admin/sections/${section.key}/publish`);
            toast.success(publish ? "Section publiée sur le site." : "Brouillon enregistré.");
            onSaved();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const revert = async (index) => {
        try {
            await api.post(`/admin/sections/${section.key}/revert`, { index });
            toast.success("Version restaurée en brouillon. Publiez pour la mettre en ligne.");
            onSaved();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const openPreview = async () => {
        try {
            const r = await api.get("/admin/preview-token");
            window.open(`/preview/${r.data.token}${PREVIEW_PATH[section.key] || ""}`, "_blank", "noopener");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const pick = (m) => {
        setForm({ ...form, [picker === "video" ? "video_media_id" : "image_media_id"]: m.media_id });
        if (m.status !== "publie") toast.info(`« ${m.title} » est en brouillon : publiez-le dans Médias & Vidéos pour l'afficher publiquement.`);
        setPicker(null);
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="bg-cream max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="section-editor">
                <DialogHeader>
                    <DialogTitle className="font-heading text-forest">{SECTION_LABELS[section.key] || section.key}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><Label>Surtitre (eyebrow)</Label><Input value={form.eyebrow || ""} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} data-testid="section-eyebrow-input" /></div>
                        <div className="flex items-end pb-1">
                            <label className="flex items-center gap-3 text-sm text-pineneedle">
                                <Switch checked={form.visible !== false} onCheckedChange={(v) => setForm({ ...form, visible: v })} data-testid="section-visible-switch" /> Section visible sur le site
                            </label>
                        </div>
                    </div>
                    <div><Label>Titre</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="section-title-input" /></div>
                    <div><Label>Sous-titre</Label><Input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="section-subtitle-input" /></div>
                    <div>
                        <Label>Texte (mise en forme premium)</Label>
                        <RichTextEditor value={rich} onChange={setRich} testid="section-body-editor" />
                    </div>
                    {Array.isArray(form.bullets) && (
                        <div>
                            <Label>Points clés (liste)</Label>
                            <div className="space-y-2 mt-1">
                                {form.bullets.map((b, i) => (
                                    <div key={i} className="flex gap-2">
                                        <Input value={b} onChange={(e) => setForm({ ...form, bullets: form.bullets.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`section-bullet-${i}`} />
                                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setForm({ ...form, bullets: form.bullets.filter((_, j) => j !== i) })} data-testid={`section-bullet-del-${i}`}><Trash2 size={14} /></Button>
                                    </div>
                                ))}
                                <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => setForm({ ...form, bullets: [...form.bullets, ""] })} data-testid="section-bullet-add"><Plus size={13} /> Ajouter un point</Button>
                            </div>
                        </div>
                    )}
                    {(form.note !== undefined || section.key === "pillar_balance") && (
                        <div><Label>Note / mention légale</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="section-note-input" /></div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Libellé du bouton</Label><Input value={form.cta_label || ""} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="section-cta-label" /></div>
                        <div><Label>Lien du bouton</Label><Input value={form.cta_href || ""} onChange={(e) => setForm({ ...form, cta_href: e.target.value })} data-testid="section-cta-href" /></div>
                    </div>

                    {(caps.image || caps.video) && (
                        <div className="rounded-2xl border border-gold/30 bg-white p-4 space-y-3" data-testid="section-media-block">
                            <p className="text-sm font-semibold text-forest">Média de la section</p>
                            {caps.image && (
                                <div>
                                    <Label className="text-xs">Image</Label>
                                    {form.image_media_id ? (
                                        <div className="flex items-center gap-3 mt-1.5 bg-ivory border rounded-xl p-3">
                                            <img src={`${API}/media/file/${form.image_media_id}`} alt={curImage?.alt || ""} className="w-16 h-16 rounded-lg object-cover bg-pearl" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-forest truncate">{curImage?.title || form.image_media_id}</p>
                                                <p className="text-[11px] text-pineneedle/70">{curImage ? (curImage.status === "publie" ? "Publiée" : "Brouillon — invisible publiquement") : "Hors médiathèque"}</p>
                                            </div>
                                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setPicker("image")} data-testid="section-image-replace">Remplacer</Button>
                                            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setForm({ ...form, image_media_id: "" })} data-testid="section-image-clear"><X size={13} /> Retirer</Button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setPicker("image")} data-testid="section-image-pick"><ImageIcon size={13} /> Choisir une image</Button>
                                            <span className="text-[11px] text-pineneedle/70">Aucune image — état neutre affiché publiquement.</span>
                                        </div>
                                    )}
                                    {form.image_media_id && (
                                        <div className="mt-2 max-w-56">
                                            <Label className="text-xs">Cadrage de l'image</Label>
                                            <Select value={form.image_fit || "cover"} onValueChange={(v) => setForm({ ...form, image_fit: v })}>
                                                <SelectTrigger className="h-8 text-xs bg-white" data-testid="section-image-fit"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="cover" data-testid="fit-cover">Couvrir (recadrage autorisé)</SelectItem>
                                                    <SelectItem value="contain" data-testid="fit-contain">Contenir (image intégrale)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>
                            )}
                            {caps.video && (
                                <div>
                                    <Label className="text-xs">Vidéo</Label>
                                    {form.video_media_id ? (
                                        <div className="flex items-center gap-3 mt-1.5 bg-ivory border rounded-xl p-3">
                                            <div className="w-16 h-16 rounded-lg bg-forest-deep flex items-center justify-center shrink-0"><Film size={20} className="text-gold" /></div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-forest truncate">{curVideo?.title || form.video_media_id}</p>
                                                <p className="text-[11px] text-pineneedle/70">{curVideo ? (curVideo.status === "publie" ? "Publiée" : "Brouillon — invisible publiquement") : "Hors médiathèque"}</p>
                                            </div>
                                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setPicker("video")} data-testid="section-video-replace">Remplacer</Button>
                                            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setForm({ ...form, video_media_id: "" })} data-testid="section-video-clear"><X size={13} /> Retirer</Button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setPicker("video")} data-testid="section-video-pick"><Film size={13} /> Choisir une vidéo</Button>
                                            <span className="text-[11px] text-pineneedle/70">Aucune vidéo — état « bientôt disponible » affiché.</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {form.image_media_id && form.video_media_id && (
                                <div className="max-w-64">
                                    <Label className="text-xs">Média affiché</Label>
                                    <Select value={form.media_display || "auto"} onValueChange={(v) => setForm({ ...form, media_display: v })}>
                                        <SelectTrigger className="h-8 text-xs bg-white" data-testid="section-media-display"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="auto">Automatique</SelectItem>
                                            <SelectItem value="image">Image uniquement</SelectItem>
                                            <SelectItem value="video">Vidéo uniquement</SelectItem>
                                            <SelectItem value="none">Aucun média</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="rounded-2xl border border-border bg-ivory/60 p-4" data-testid="section-admin-preview">
                        <p className="text-xs font-mono uppercase tracking-widest text-gold-dark mb-2">Aperçu immédiat</p>
                        {form.eyebrow && <p className="eyebrow mb-1">{form.eyebrow}</p>}
                        <p className="font-heading font-bold text-ink text-lg">{form.title || "—"}</p>
                        {form.subtitle && <p className="text-sm text-ink-soft mt-1">{form.subtitle}</p>}
                        <RichText html={rich} className="text-sm text-ink-soft mt-2" />
                    </div>

                    {section.draft && <p className="text-xs bg-amber-100 text-amber-900 rounded-lg px-3 py-2">Un brouillon non publié existe pour cette section.</p>}
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={() => save(false)} data-testid="section-save-draft">Enregistrer le brouillon</Button>
                        <Button className="bg-forest text-cream gap-1" onClick={() => save(true)} data-testid="section-publish"><UploadCloud size={15} /> Publier</Button>
                        <Button variant="outline" className="gap-1" onClick={openPreview} data-testid="section-preview-page"><ExternalLink size={14} /> Prévisualiser la page</Button>
                        {(section.revisions || []).length > 0 && (
                            <Button variant="ghost" className="gap-1" onClick={() => setShowHistory(!showHistory)} data-testid="section-history"><History size={15} /> Versions ({section.revisions.length})</Button>
                        )}
                    </div>
                    {showHistory && (
                        <div className="space-y-2 border-t pt-3">
                            {section.revisions.map((r, i) => (
                                <div key={i} className="flex items-center justify-between text-xs bg-white rounded-lg border p-3">
                                    <span className="text-pineneedle">Version du {fmtDate(r.at)} par {r.by}</span>
                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => revert(i)} data-testid={`section-revert-${i}`}>Restaurer</Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {picker && <MediaPicker open kind={picker} selectedId={picker === "video" ? form.video_media_id : form.image_media_id} onClose={() => setPicker(null)} onSelect={pick} />}
            </DialogContent>
        </Dialog>
    );
}

function PageEditor({ page, onClose, onSaved }) {
    const [form, setForm] = useState({ title: page.title, status: page.status });
    const [rich, setRich] = useState(page.body_html || plainToHtml(page.body));
    const save = async () => {
        try {
            await api.patch(`/admin/pages/${page.slug}`, { title: form.title, status: form.status, body_html: rich, body: htmlToPlain(rich) });
            toast.success("Page enregistrée.");
            onSaved();
        } catch (e) {
            toast.error(apiError(e));
        }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="bg-cream max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="page-editor">
                <DialogHeader><DialogTitle className="font-heading text-forest">{PAGE_LABELS[page.slug] || page.slug}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="page-title-input" /></div>
                    <div><Label>Contenu</Label><RichTextEditor value={rich} onChange={setRich} testid="page-body-editor" minHeight="320px" /></div>
                    <label className="flex items-center gap-3 text-sm text-pineneedle">
                        <Switch checked={form.status === "published"} onCheckedChange={(v) => setForm({ ...form, status: v ? "published" : "brouillon" })} data-testid="page-status-switch" /> Page publiée
                    </label>
                    {(rich.includes("[À compléter")) && (
                        <p className="text-xs bg-amber-100 text-amber-900 rounded-lg px-3 py-2">Ce document contient des mentions « À compléter » : renseignez-les avant la mise en production.</p>
                    )}
                    <Button onClick={save} className="bg-forest text-cream" data-testid="page-save">Enregistrer</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function Content() {
    const [sections, setSections] = useState(null);
    const [pages, setPages] = useState(null);
    const [library, setLibrary] = useState([]);
    const [tab, setTab] = useState("sections");
    const [editing, setEditing] = useState(null);
    const [editingPage, setEditingPage] = useState(null);

    const load = () => {
        api.get("/admin/sections").then((r) => setSections(r.data)).catch((e) => toast.error(apiError(e)));
        api.get("/admin/pages").then((r) => setPages(r.data)).catch((e) => toast.error(apiError(e)));
        api.get("/admin/media", { params: { limit: 500 } }).then((r) => setLibrary(r.data.items || [])).catch((e) => toast.error(apiError(e)));
    };
    useEffect(() => { load(); }, []);

    const togglePublish = async (s) => {
        try {
            if (s.status === "published") await api.post(`/admin/sections/${s.key}/unpublish`);
            else await api.post(`/admin/sections/${s.key}/publish`);
            toast.success(s.status === "published" ? "Section masquée." : "Section publiée.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    return (
        <div data-testid="admin-content">
            <PageHeader title="Contenu du site" subtitle="Workflow : brouillon → prévisualisation → publication. Aucune modification n'est visible avant publication." />
            <div className="flex gap-2 mb-6">
                {[["sections", "Sections du site"], ["pages", "Pages légales"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full text-sm border ${tab === k ? "bg-forest text-cream border-forest" : "border-border text-pineneedle"}`} data-testid={`content-tab-${k}`}>{l}</button>
                ))}
            </div>
            {tab === "sections" && (
                <div className="rounded-2xl bg-white border border-gold/25 shadow-sm divide-y" data-testid="sections-list">
                    {(sections || []).map((s) => {
                        const data = s.draft || s.published || {};
                        const hasMedia = MEDIA_CAPS[s.key];
                        return (
                            <div key={s.key} className="flex items-center gap-4 p-4" data-testid={`section-row-${s.key}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-forest text-sm">{SECTION_LABELS[s.key] || s.key}</p>
                                    <p className="text-xs text-pineneedle truncate">{data.title}</p>
                                </div>
                                {hasMedia && (
                                    <span className="hidden sm:flex text-[10px] items-center gap-1 text-pineneedle/70" title="Médias associés">
                                        {data.image_media_id && <ImageIcon size={12} className="text-forest" />}
                                        {data.video_media_id && <Film size={12} className="text-forest" />}
                                    </span>
                                )}
                                <span className={`text-xs px-2 py-1 rounded-full ${s.status === "published" ? (s.draft ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800") : "bg-gray-200 text-gray-700"}`}>
                                    {s.status === "published" ? (s.draft ? "Publié + brouillon" : "Publié") : "Masqué"}
                                </span>
                                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setEditing(s)} data-testid={`edit-section-${s.key}`}><Pencil size={13} /> Modifier</Button>
                                <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => togglePublish(s)} data-testid={`toggle-section-${s.key}`}>
                                    {s.status === "published" ? <><EyeOff size={13} /> Masquer</> : <><Eye size={13} /> Publier</>}
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
            {tab === "pages" && (
                <div className="rounded-2xl bg-white border border-gold/25 shadow-sm divide-y" data-testid="pages-list">
                    {(pages || []).map((p) => (
                        <div key={p.slug} className="flex items-center gap-4 p-4" data-testid={`page-row-${p.slug}`}>
                            <div className="flex-1">
                                <p className="font-semibold text-forest text-sm">{p.title}</p>
                                <p className="text-xs text-pineneedle">/{p.slug} · mis à jour le {fmtDate(p.updated_at)}</p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${p.status === "published" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>{p.status === "published" ? "Publiée" : "Brouillon"}</span>
                            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setEditingPage(p)} data-testid={`edit-page-${p.slug}`}><Pencil size={13} /> Modifier</Button>
                        </div>
                    ))}
                </div>
            )}
            {editing && <SectionEditor section={editing} library={library} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {editingPage && <PageEditor page={editingPage} onClose={() => setEditingPage(null)} onSaved={() => { setEditingPage(null); load(); }} />}
        </div>
    );
}
